/**
 * Bulk client import modal — download template, upload xlsx, poll progress,
 * download annotated result. Used on /app/clients.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, X, CheckCircle2, AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { fmtDateTime } from "@/lib/time";

export default function BulkImportModal({ onClose, onListRefresh }) {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [job, setJob] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const pollRef = useRef(null);
    const inputRef = useRef(null);

    const stopPoll = useCallback(() => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }, []);

    useEffect(() => () => stopPoll(), [stopPoll]);

    const pickFile = (f) => {
        if (!f) return;
        if (!f.name.toLowerCase().endsWith(".xlsx")) {
            toast.error("Only .xlsx files are supported");
            return;
        }
        if (f.size > 5 * 1024 * 1024) {
            toast.error("File too large (max 5MB)");
            return;
        }
        setFile(f);
    };

    const downloadTemplate = async () => {
        try {
            const res = await api.get("/clients/import/template", { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = "lawos-bulk-client-template.xlsx";
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(apiError(e)); }
    };

    const upload = async () => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/clients/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setJob({ id: data.job_id, status: data.status, rows_total: data.rows_total,
                       rows_done: 0, rows_succeeded: 0, rows_errored: 0,
                       cases_linked: 0, ecourts_attempted: 0, ecourts_succeeded: 0,
                       sample_errors: [] });
            // Start polling
            pollRef.current = setInterval(async () => {
                try {
                    const { data: s } = await api.get(`/clients/import/${data.job_id}`);
                    setJob(s);
                    if (s.status === "completed" || s.status === "crashed") {
                        stopPoll();
                        if (s.status === "completed") {
                            const failed = (s.rows_errored || 0) + (s.rows_partial || 0);
                            toast.success(`Import done · ${s.rows_succeeded}/${s.rows_total} succeeded · ${s.cases_linked} cases linked${failed ? ` · ${failed} need attention` : ""}`);
                            // Refresh the underlying clients list either way.
                            onListRefresh && onListRefresh();
                        } else {
                            toast.error("Import job crashed — check sample errors");
                        }
                    }
                } catch (e) { stopPoll(); toast.error(apiError(e)); }
            }, 1200);
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setUploading(false);
        }
    };

    const downloadResult = async () => {
        if (!job) return;
        try {
            const res = await api.get(`/clients/import/${job.id}/result`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `bulk-import-${job.id}-result.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(apiError(e)); }
    };

    const resumeFailed = async () => {
        if (!job) return;
        const remaining = (job.rows_errored || 0) + (job.rows_partial || 0);
        if (!window.confirm(`Retry the ${remaining} failed/partial row${remaining === 1 ? "" : "s"}? Already-created clients & cases will NOT be duplicated.`)) return;
        try {
            const { data } = await api.post(`/clients/import/${job.id}/resume`);
            toast.success(`Resuming · retrying ${data.retrying_rows} rows`);
            setJob({ ...job, status: "processing", completed_at: null });
            stopPoll();
            pollRef.current = setInterval(async () => {
                try {
                    const { data: s } = await api.get(`/clients/import/${job.id}`);
                    setJob(s);
                    if (s.status === "completed" || s.status === "crashed") {
                        stopPoll();
                        if (s.status === "completed") {
                            toast.success(`Resume done · ${s.rows_succeeded}/${s.rows_total} succeeded · ${s.rows_partial} partial · ${s.rows_errored} still failed`);
                            onListRefresh && onListRefresh();
                        } else {
                            toast.error("Resume crashed");
                        }
                    }
                } catch (e) { stopPoll(); toast.error(apiError(e)); }
            }, 1200);
        } catch (e) { toast.error(apiError(e)); }
    };

    const rollback = async () => {
        if (!job) return;
        const totalToDelete = (job.rows_succeeded || 0) + (job.rows_partial || 0);
        const confirmText = window.prompt(
            `⚠️ ROLLBACK this import?\n\n` +
            `This will hard-delete ${totalToDelete} client${totalToDelete === 1 ? "" : "s"} + their cases created by this job.\n\n` +
            `THIS CANNOT BE UNDONE.\n\n` +
            `Type DELETE (uppercase) to confirm:`
        );
        if (confirmText !== "DELETE") { toast.info("Cancelled"); return; }
        try {
            const { data } = await api.post(`/clients/import/${job.id}/rollback`,
                { confirmation: "DELETE" });
            toast.success(`Rolled back · ${data.deleted_clients} clients + ${data.deleted_cases} cases deleted`);
            // Refresh job status
            const { data: s } = await api.get(`/clients/import/${job.id}`);
            setJob(s);
            onListRefresh && onListRefresh();
        } catch (e) { toast.error(apiError(e)); }
    };

    const reset = () => {
        stopPoll();
        setFile(null); setJob(null);
        if (inputRef.current) inputRef.current.value = "";
    };

    const backfillTitles = async () => {
        if (!window.confirm(
            "Re-fetch eCourts data for cases that still have an empty title?\n\n" +
            "Every fetch is billed at the normal per-CNR rate. Failed lookups are NOT charged.\n\n" +
            "Continue?"
        )) return;
        try {
            const { data } = await api.post("/clients/import/backfill/titles");
            if (data.total === 0) {
                toast.info(data.message || "Nothing to backfill.");
            } else {
                toast.success(`Backfill · repaired ${data.repaired}/${data.total} · ${data.failed} failed`);
            }
            onListRefresh && onListRefresh();
        } catch (e) { toast.error(apiError(e)); }
    };

    const pct = job && job.rows_total > 0 ? Math.round((job.rows_done / job.rows_total) * 100) : 0;
    const inFlight = job && job.status === "processing";

    return (
        <div className="fixed inset-0 bg-background/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={e => e.stopPropagation()}
                className="bg-card border border-border w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto"
                data-testid="bulk-import-modal">
                <div className="flex justify-between items-start">
                    <div>
                        <h3 className="text-xl text-foreground">Bulk client import</h3>
                        <p className="text-xs text-muted-foreground mt-1">Upload an Excel and we&apos;ll create clients + auto-fetch case details from eCourts where you give a CNR.</p>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5"/></button>
                </div>

                {!job && (
                    <>
                        {/* Step 1 — Template */}
                        <div className="border border-border bg-background p-4 flex items-start gap-3" data-testid="bulk-step-template">
                            <FileSpreadsheet className="w-5 h-5 text-primary mt-0.5 shrink-0"/>
                            <div className="flex-1">
                                <div className="text-sm text-foreground">1 · Download the template</div>
                                <div className="text-xs text-muted-foreground mt-0.5">Excel with all supported columns + an Instructions tab. Use it as the starting point.</div>
                            </div>
                            <button onClick={downloadTemplate} data-testid="bulk-download-template"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em]">
                                <Download className="w-3 h-3"/>Download template
                            </button>
                        </div>

                        {/* Step 2 — Upload */}
                        <div className="border border-border bg-background p-4" data-testid="bulk-step-upload">
                            <div className="text-sm text-foreground">2 · Upload your filled Excel</div>
                            <div className="text-xs text-muted-foreground mt-0.5">.xlsx only · max 5MB · max 1000 rows per upload.</div>
                            <div
                                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={e => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files[0]); }}
                                onClick={() => inputRef.current?.click()}
                                className={`mt-3 border-2 border-dashed ${dragOver ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground"} p-6 text-center cursor-pointer transition-colors`}
                                data-testid="bulk-dropzone">
                                <Upload className="w-6 h-6 text-muted-foreground mx-auto mb-2"/>
                                {file ? (
                                    <div className="text-sm text-foreground">{file.name} <span className="text-muted-foreground text-xs">({(file.size / 1024).toFixed(1)} KB)</span></div>
                                ) : (
                                    <>
                                        <div className="text-xs text-foreground">Drop .xlsx here</div>
                                        <div className="text-[10px] text-muted-foreground mt-1">or click to choose</div>
                                    </>
                                )}
                            </div>
                            <input ref={inputRef} type="file" accept=".xlsx"
                                data-testid="bulk-file-input"
                                onChange={e => pickFile(e.target.files[0])} className="hidden"/>
                            {file && (
                                <div className="mt-3 flex justify-end gap-2">
                                    <button onClick={reset} className="text-xs text-muted-foreground hover:text-foreground">Clear</button>
                                    <button onClick={upload} disabled={uploading} data-testid="bulk-upload-submit"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em] disabled:opacity-50">
                                        {uploading ? <Loader2 className="w-3 h-3 animate-spin"/> : <Upload className="w-3 h-3"/>}
                                        Start import
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Notes */}
                        <div className="text-[10px] text-muted-foreground space-y-1">
                            <div>• If you fill <span className="text-primary">CNR (eCourts)</span> on a row, the litigation is auto-created and populated from the eCourts API.</div>
                            <div>• Each eCourts lookup is billed at your firm&apos;s usual rate — see <span className="text-primary">/app/wallet</span>. Failed lookups are not charged.</div>
                            <div>• Only the <span className="text-primary">Name</span> column is mandatory.</div>
                        </div>

                        {/* Repair — fix cases previously imported with empty title */}
                        <div className="border border-dashed border-border bg-background/60 p-3 flex items-start gap-3" data-testid="bulk-step-backfill">
                            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0"/>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-foreground">Repair earlier imports</div>
                                <div className="text-[10px] text-muted-foreground mt-0.5">If cases from an earlier bulk upload landed with an empty <span className="text-primary">Title</span>, this re-fetches eCourts for them and populates the title, parties, court history and orders.</div>
                            </div>
                            <button onClick={backfillTitles} data-testid="bulk-backfill-titles"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-amber-500/60 text-amber-600 hover:bg-amber-500/10 text-[10px] uppercase tracking-[0.16em] whitespace-nowrap">
                                <Download className="w-3 h-3"/>Backfill titles
                            </button>
                        </div>
                    </>
                )}

                {job && (
                    <div className="space-y-3" data-testid="bulk-progress">
                        <div className="border border-border bg-background p-4 space-y-3">
                            <div className="flex justify-between items-center">
                                <div className="text-sm text-foreground">{job.filename || "Import"} · {job.rows_done}/{job.rows_total} rows · {job.status}</div>
                                {job.status === "completed" && <CheckCircle2 className="w-5 h-5 text-emerald-400"/>}
                                {job.status === "crashed" && <AlertTriangle className="w-5 h-5 text-rose-400"/>}
                                {inFlight && <Loader2 className="w-5 h-5 text-primary animate-spin"/>}
                            </div>
                            <div className="h-2 bg-card rounded">
                                <div className="h-2 bg-primary rounded transition-all" style={{ width: `${pct}%` }}/>
                            </div>
                            <div className="grid grid-cols-5 gap-2 text-center text-[10px] uppercase tracking-[0.16em]">
                                <Stat label="Succeeded" value={job.rows_succeeded} color="text-emerald-400"/>
                                <Stat label="Partial" value={job.rows_partial || 0} color="text-amber-400"/>
                                <Stat label="Errored" value={job.rows_errored} color="text-rose-400"/>
                                <Stat label="Cases linked" value={job.cases_linked} color="text-primary"/>
                                <Stat label="eCourts hit" value={`${job.ecourts_succeeded}/${job.ecourts_attempted}`} color="text-foreground"/>
                            </div>
                            {job.resume_count > 0 && (
                                <div className="text-[10px] text-muted-foreground text-center">Resumed {job.resume_count}× from a prior run</div>
                            )}
                            {job.rolled_back_at && (
                                <div className="text-[10px] text-rose-400 text-center border-t border-card pt-2 mt-2">
                                    ⚠️ This import was rolled back on {fmtDateTime(job.rolled_back_at)} · {job.rollback_deleted_clients} clients + {job.rollback_deleted_cases} cases deleted
                                </div>
                            )}
                        </div>

                        {job.sample_errors && job.sample_errors.length > 0 && (
                            <div className="border border-rose-500/30 bg-rose-500/5 p-3" data-testid="bulk-sample-errors">
                                <div className="text-[10px] uppercase tracking-[0.16em] text-rose-400 mb-2">Sample row errors ({job.sample_errors.length})</div>
                                {job.sample_errors.map((e, i) => (
                                    <div key={i} className="text-[11px] text-[#E0E0E0] py-0.5">
                                        <span className="text-primary">{e.name}</span> · <span className="text-rose-400">{e.error}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="flex justify-between">
                            <button onClick={reset} className="text-xs text-muted-foreground hover:text-foreground" data-testid="bulk-new-upload">Start another upload</button>
                            <div className="flex gap-2 flex-wrap">
                                {job.status === "completed" && ((job.rows_errored || 0) + (job.rows_partial || 0)) > 0 && (
                                    <button onClick={resumeFailed} data-testid="bulk-resume-failed"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 text-[10px] uppercase tracking-[0.16em]">
                                        <Loader2 className="w-3 h-3"/>Resume failed rows ({(job.rows_errored || 0) + (job.rows_partial || 0)})
                                    </button>
                                )}
                                {job.status === "completed" && !job.rolled_back_at && (
                                    <button onClick={rollback} data-testid="bulk-rollback"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 text-[10px] uppercase tracking-[0.16em]">
                                        <Trash2 className="w-3 h-3"/>Rollback this import
                                    </button>
                                )}
                                <button onClick={downloadResult} data-testid="bulk-download-result"
                                    disabled={!(job.status === "completed" || job.rows_done > 0)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em] disabled:opacity-30">
                                    <Download className="w-3 h-3"/>Download result xlsx
                                </button>
                                <button onClick={onClose} className="px-3 py-1.5 border border-border text-muted-foreground hover:text-foreground text-[10px] uppercase tracking-[0.16em]">Close</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value, color }) {
    return (
        <div className="border border-border py-1.5 px-1">
            <div className="text-muted-foreground">{label}</div>
            <div className={`text-base font-mono ${color}`}>{value}</div>
        </div>
    );
}
